import { defineStore } from 'pinia'
import { toast } from 'vue3-toastify';
import { et, ru, enUS } from "date-fns/locale"

export const useMainStore = defineStore('main', {
    state: () => ({
        stateLoaded: false,
        locale: 'et',
        step: 'intro',
        steps: ['terms', 'privacy', 'thank-you', 'intro', 'adType', 'newspapers', 'client', 'adContent', 'dates', 'payment'],
        webSite: {},
        isLoading: false,
        stepInfo: {
            adType: null,
            newspapers: null,
            client: {
                type: "person",
                name: null,
                reg_code: null,
                phone: null,
                email: null,
                want_mail: false
            },
            content: {
                advertisement: {
                    category: null,
                    adContent: "",
                    hasFrame: false,
                    hasBackground: false,
                    hasImage: false,
                    imgUrl: null,
                    wantTranslation: true,
                    translations: {
                        'ru': "",
                        'en': ""
                    },
                },
                greeting: {
                    name: "",
                    beginningText: "",
                    endingText: "",
                    hasPoem: false,
                    hasSymbol: false,
                    hasImage: false,
                    poem: null,
                    customPoem: "",
                    symbol: null,
                    imgUrl: null,
                },
                condolence: {
                    name: "",
                    funeral: "",
                    bornDate: null,
                    deathDate: null,
                    beginningText: "",
                    endingText: "",
                    hasPoem: false,
                    hasSymbol: false,
                    hasImage: false,
                    poem: null,
                    customPoem: "",
                    symbol: null,
                    imgUrl: null,
                }
            },
            dates: {
                selectedDates: {}
            }
        }
    }),
    getters: {
        datepickerLocale: state => {
            if (state.locale === 'et') {
                return et;
            } else if (state.locale === 'en') {
                return enUS;
            } else if (state.locale === 'ru') {
                return ru;
            }
            return et;
        },
        introText: state => {
            if (!state.webSite) {
                return "";
            }
            return state.webSite['intro_' + state.locale];
        },
        termsText: state => {
            if (!state.webSite) {
                return "";
            }
            return state.webSite['terms_' + state.locale];
        },
        privacyText: state => {
            if (!state.webSite) {
                return "";
            }
            return state.webSite['privacy_' + state.locale];
        },
        contactsText: state => {
            if (!state.webSite) {
                return "";
            }
            return state.webSite['contacts_' + state.locale];
        },
        translationNeeds: state => {
            let needs = {};
            if (!state.stepInfo.newspapers) {
                return needs;
            }

            state.stepInfo.newspapers.forEach(paper => {
                if (!paper.selected) {
                    return;
                }
                if (paper.lang.split(",").includes('ru')) {
                    needs['ru'] = "Tolge vene keeles";
                }
                if (paper.lang.split(",").includes('en')) {
                    needs['en'] = "Tolge inglise keeles";
                }
            });
            return needs;
        },
        singlePrices: state => {
            let singlePrices = [];
            if (!state.stepInfo.newspapers) {
                return singlePrices;
            }
            let translationPrice = 0;
            state.stepInfo.newspapers.forEach(paper => {
                if (!paper.selected) {
                    return;
                }
                let priceInfo = state.stepInfo.client && state.stepInfo.client.type === "company" ? paper.business_prices : paper.private_prices;

                let singlePrice = {
                    name: paper.name,
                    single_price: 0
                };
                if (state.stepInfo.adType === "advertisement") {
                    let adInfo = state.stepInfo.content.advertisement;
                    if (adInfo === null || !adInfo.adContent) {
                        return;
                    }
                    singlePrice.single_price = priceInfo.letter_price * adInfo.adContent.length;
                    if (adInfo.hasFrame) {
                        singlePrice.single_price += priceInfo.frame_price;
                    }
                    if (adInfo.hasBackground) {
                        singlePrice.single_price += priceInfo.background_price;
                    }
                    if (adInfo.hasImage) {
                        singlePrice.single_price += priceInfo.image_price;
                    }
                    if (adInfo.wantTranslation && translationPrice === 0) {
                        translationPrice = priceInfo.translation_price;
                    }
                } else if (state.stepInfo.adType === "condolence") {
                    let condoInfo = state.stepInfo.content.condolence;
                    if (condoInfo === null) {
                        return singlePrices;
                    }
                    if (!condoInfo.hasPoem && !(condoInfo.hasImage || condoInfo.hasSymbol)) {
                        singlePrice.single_price = priceInfo.condo_small_price;
                    } else if (condoInfo.hasPoem && (condoInfo.hasImage || condoInfo.hasSymbol)) {
                        singlePrice.single_price = priceInfo.condo_large_price;
                    } else {
                        singlePrice.single_price = priceInfo.condo_medium_price;
                    }
                } else if (state.stepInfo.adType === "greeting") {
                    let greetInfo = state.stepInfo.content.greeting;
                    if (greetInfo === null) {
                        return singlePrices;
                    }
                    if (!greetInfo.hasPoem && !(greetInfo.hasImage || greetInfo.hasSymbol)) {
                        singlePrice.single_price = priceInfo.greet_small_price;
                    } else if (greetInfo.hasPoem && (greetInfo.hasImage || greetInfo.hasSymbol)) {
                        singlePrice.single_price = priceInfo.greet_large_price;
                    } else {
                        singlePrice.single_price = priceInfo.greet_medium_price;
                    }
                }

                singlePrice.single_price = singlePrice.single_price.toFixed(2);
                singlePrice.slug = paper.slug;
                singlePrices.push(singlePrice);
            });
            if (translationPrice > 0) {
                let translationPriceObject = {
                    isTranslation: true,
                    single_price: translationPrice.toFixed(2)
                };
                singlePrices.push(translationPriceObject);
            }
            return singlePrices;
        }
    },
    actions: {
        sessExpired() {
            toast.info('Sessioon on aegunud sisesta kuulutus uuesti');
            console.log("Session expired, restarting");
            this.setAdType(null);
        },
        setWebsite(webSite) {
            this.webSite = webSite;
        },
        setLocale(locale) {
            this.locale = locale;
        },
        changeStep(step) {
            if (this.steps.indexOf(step) < 0) {
                console.log("This step not available")
                step = 'intro';
            }

            window.location.hash = "#" + step;
            this.step = step;
        },
        setIsLoading(isLoading) {
            this.isLoading = isLoading;
        },
        setAdType(adType) {
            this.stepInfo.adType = adType;
        },
        setNewspapers(newspapers) {
            this.stepInfo.newspapers = newspapers;
        },
        setClient(client) {
            this.stepInfo.client = client;
        },
        setAdvertisement(advertisement) {
            this.stepInfo.content.advertisement = advertisement;
        },
        setGreeting(greeting) {
            this.stepInfo.content.greeting = greeting;
        },
        setCondolence(condolence) {
            this.stepInfo.content.condolence = condolence;
        },
        setDates(dates) {
            this.stepInfo.dates.selectedDates = dates.selectedDates;
        },
        setStateLoaded() {
            this.stateLoaded = true;
        }
    },
});
